import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { ValidationErrors } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { FailedImport, FailedParse, FailedValidationImport, ImportErrorTypes, ImportResponse, ImportStatus, SkippedImport, SuccessFullImport } from '@shared/services/export-import-services/interfaces';
import { Observable } from 'rxjs';

/**
 * One rendered line in the result dialog.
 *
 * Just a message: whatever address the user needs is already inside it. A parse error has no entity `Id`, so the reading transport folds its own locator into the text — a row number for a spreadsheet, something else for a format that has no rows.
 */
export interface ImportResultLine {
	message: string;
}

/** Lets a caller override how the successful list is ordered. */
export interface ImportErrorPopupData extends ImportResponse {
	sortSuccessfulImports?: (a: SuccessFullImport, b: SuccessFullImport) => number;
}

const defaultSuccessfulImportsSort = (a: SuccessFullImport, b: SuccessFullImport): number => a.Id.localeCompare(b.Id, 'da');

@Component({
	selector: 'app-import-error-popup',
	templateUrl: './import-error-popup.component.html',
	styleUrl: './import-error-popup.component.scss',
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class ImportErrorPopupComponent {
	/** The import result, normalised so a missing payload renders the empty state rather than throwing. */
	public data: ImportErrorPopupData;

	/** Real failures only — `ImportStatus.Skipped` rows are reported separately in `skipped`, because a skip is a deliberate no-op and reading it as a failure told the user three rows had broken when nothing had. */
	public errors: ImportResultLine[] = [];

	/** Rows left untouched because the entity already existed and `overwrite` was not ticked. */
	public skipped: ImportResultLine[] = [];

	/** Sorted copy — the response arrives in completion order, which is not readable. */
	public successfulImports: SuccessFullImport[] = [];

	public createdCount: number = 0;
	public updatedCount: number = 0;
	public skippedCount: number = 0;

	/** Failing _rows_, not failing messages — one validation row can produce several messages, so this is deliberately not `errors.length`. Rendered explicitly so a total-failure import shows a number instead of a bare "Import failed". */
	public errorCount: number = 0;

	public totalImports: number = 0;

	constructor(
		public dialogRef: MatDialogRef<ImportErrorPopupComponent>,
		public translator: TranslateService,
		@Inject(MAT_DIALOG_DATA) data: ImportErrorPopupData | null,
	) {
		// MAT_DIALOG_DATA is null when the dialog is opened without a result payload. Normalising
		// here is what makes that case render an empty state: the previous guard assigned
		// `this.errors` and then dereferenced `data` on the very next statement anyway.
		this.data = data ?? { successfulImports: [], errors: [] };

		this.successfulImports = [...this.data.successfulImports].sort(this.data.sortSuccessfulImports ?? defaultSuccessfulImportsSort);

		// One pass: partitions the entries AND counts them. The counts are per *row*, which is why
		// they cannot be read off `errors.length` afterwards — one validation row expands into
		// several rendered lines.
		for (const error of this.data.errors) {
			// Checked first so everything below is, by definition, one failing row. `Failed` and
			// `Skipped` share a shape, so the status is all that separates a real failure from a row
			// the user asked to leave alone.
			if (this.isSkipped(error)) {
				this.skipped.push({ message: this.withAddress(error, error.message) });
				this.skippedCount++;
				continue;
			}

			this.errorCount++;

			if (this.isParseError(error)) {
				this.errors.push({ message: error.message });
			} else if (this.isValidationError(error)) {
				this.errors.push(...this.formatValidationErrorResults(error));
			} else if (this.isFailedImport(error)) {
				this.errors.push({ message: this.withAddress(error, error.message) });
			} else {
				// Counted above regardless — an unrecognised status is still a row that did not import.
				console.warn('Unknown error type', error);
			}
		}

		// Both statuses are already on every successful import and were previously collapsed into
		// one undifferentiated list of ids.
		for (const success of this.data.successfulImports) {
			if (success.status === ImportStatus.SuccessCreate) {
				this.createdCount++;
			} else if (success.status === ImportStatus.SuccessUpdate) {
				this.updatedCount++;
			}
		}

		this.totalImports = this.data.successfulImports.length + this.data.errors.length;
	}

	/** Nothing to report at all — a null payload, or a response with no rows in it. */
	get isEmpty(): boolean {
		return this.totalImports === 0;
	}

	/** A create/update outcome the import chose not to write, or could not. Deliberately covers both `Failed` and `Skipped`: they are the same shape, and a type predicate per status would narrow the union to `never` in whichever branch came second. */
	isFailedImport(error: ImportErrorTypes): error is FailedImport {
		return error.status === ImportStatus.Failed || error.status === ImportStatus.Skipped;
	}

	/** A deliberate no-op, not a failure — the row already existed and `overwrite` was not ticked. */
	isSkipped(error: ImportErrorTypes): error is SkippedImport {
		return error.status === ImportStatus.Skipped;
	}

	isParseError(error: ImportErrorTypes): error is FailedParse {
		return error.status === ImportStatus.ParsingError;
	}

	isValidationError(error: ImportErrorTypes): error is FailedValidationImport {
		return error.status === ImportStatus.ValidationError;
	}

	getHeaderText(): Observable<string> {
		if (this.isEmpty) {
			return this.translator.get('shared.components.import-error-popup.empty.header');
		}
		// Skipped rows are not failures, so a run that only skipped rows reads as a success and
		// says so in its own section.
		else if (this.errorCount === 0) {
			return this.translator.get('shared.components.import-error-popup.success.header');
		} else if (this.data.successfulImports.length === 0) {
			return this.translator.get('shared.components.import-error-popup.error.header');
		} else {
			return this.translator.get('shared.components.import-error-popup.partial-success.header', {
				totalCount: this.totalImports,
				errorCount: this.errorCount,
			});
		}
	}

	/**
	 * Flattens one validation row's nested `ValidationErrors` tree into rendered lines.
	 *
	 * Returns `ImportResultLine[]` rather than `string[]` so every branch of the constructor's error loop pushes the same shape. `row` is deliberately absent: a validation error carries an entity `Id`, which is prefixed onto each message, so there is no row number to address it by — unlike a parse error (see
	 * `ImportResultLine`).
	 */
	/** How to address the row in a message: where it sat in the file if the reading transport knew, otherwise the entity's own id. A row with a blank or mistyped alias has no useful id, which is exactly when the position matters. */
	private addressOf(error: { Id: string; location?: string }): string {
		return error.location ?? error.Id;
	}

	/** Prefixes a message that already names its entity with the file position, when there is one. */
	private withAddress(error: { location?: string }, message: string): string {
		return error.location ? `${error.location}: ${message}` : message;
	}

	formatValidationErrorResults(errors: FailedValidationImport): ImportResultLine[] {
		return Object.entries(errors.errors).reduce((acc, [key, value]) => {
			if (value === null || value === undefined) {
				return acc;
			}
			for (const message of this.formatValidationErrors(value, `${key}`)) {
				acc.push({ message: `${this.addressOf(errors)}: ${message}` });
			}
			return acc;
		}, [] as ImportResultLine[]);
	}

	formatValidationErrors(errors: ValidationErrors, path: string = '', acc: Array<string> = []): Array<string> {
		if (errors === null || errors === undefined) {
			return [];
		}

		if (this.isObjectErrors(errors)) {
			// Object errors
			for (const [property, propErrors] of Object.entries(errors.objectError)) {
				this.formatValidationErrors(propErrors, path !== '' ? `${path}.${property}` : property, acc);
			}
		} else if (this.isArrayErrors(errors)) {
			for (const arrErr of errors.arrayError) {
				acc.push(...this.formatValidationErrors(arrErr.errors, `${path}[${arrErr.index}]`));
			}
		} else {
			// Simple errors
			for (const errorValue of Object.values(errors)) {
				acc.push(`${path}: ${errorValue}`);
			}
		}

		return acc;
	}

	isObjectErrors(errors: Record<string, any>): errors is { objectError: ValidationErrors } {
		return 'objectError' in errors;
	}

	isArrayErrors(errors: Record<string, any>): errors is {
		arrayError: Array<{ index: number; errors: ValidationErrors }>;
	} {
		return 'arrayError' in errors;
	}

	closeDialog(): void {
		this.dialogRef.close();
	}
}
